<template>
  <div>
    <Head title="Sebaran Berdasarkan Program Studi" />

    <!-- FILTER -->
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="w-full p-4 font-semibold bg-gray-200">Filter</div>

      <div class="flex flex-wrap w-full p-6">
        <div class="w-full font-semibold lg:w-3/12">Periode Beasiswa:</div>

        <v-select
          v-model="form.periode"
          :options="periodes"
          class="w-full lg:w-9/12"
          :reduce="p => p.id"
          label="nama"
          placeholder="Pilih Periode"
        />
      </div>
    </div>
    <!-- TABEL -->
    <div class="mb-10 overflow-auto bg-white border rounded shadow">
      <div class="flex justify-end p-3 border-b bg-gray-50">
        <a
          :href="exportUrl"
          class="px-4 py-2 text-sm font-semibold text-white rounded shadow bg-green-600 hover:bg-green-700"
        >
          ⬇ Download Excel
        </a>
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-100 border-b">
            <th class="w-12 p-2 text-center border">No</th>

            <th class="p-2 border cursor-pointer" @click="toggleSort('prodi')">
              Program Studi
              <span v-if="sort.field === 'prodi'">
                {{ sort.direction === 'asc' ? '↑' : '↓' }}
              </span>
            </th>

            <th class="p-2 border cursor-pointer" @click="toggleSort('universitas')">
              Kampus
              <span v-if="sort.field === 'universitas'">
                {{ sort.direction === 'asc' ? '↑' : '↓' }}
              </span>
            </th>

            <th class="p-2 text-center border cursor-pointer" @click="toggleSort('total')">
              Total
              <span v-if="sort.field === 'total'">
                {{ sort.direction === 'asc' ? '↑' : '↓' }}
              </span>
            </th>

            <th class="p-2 text-center text-blue-600 border cursor-pointer" @click="toggleSort('laki')">
              Laki-laki
              <span v-if="sort.field === 'laki'">
                {{ sort.direction === 'asc' ? '↑' : '↓' }}
              </span>
            </th>

            <th class="p-2 text-center text-pink-600 border cursor-pointer" @click="toggleSort('perempuan')">
              Perempuan
              <span v-if="sort.field === 'perempuan'">
                {{ sort.direction === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
          </tr>
        </thead>


        <tbody>
          <tr v-for="(item, i) in sortedReport" :key="i" class="border-b hover:bg-gray-50">
            <td class="p-2 text-center border">{{ i + 1 }}</td>

            <td class="p-2 font-semibold border">
              <Link
                :href="route('report.prodi.detail', {
                  prodi: item.prodi,
                  periode: form.periode
                })"
                class="text-indigo-600 hover:underline"
              >
                {{ item.prodi }}
              </Link>
            </td>

            <td class="p-2 border">
              {{ item.universitas }}
            </td>

            <td class="p-2 font-semibold text-center border">
              {{ item.total }}
            </td>

            <td class="p-2 text-center text-blue-600 border">
              {{ item.laki }}
            </td>

            <td class="p-2 text-center text-pink-600 border">
              {{ item.perempuan }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- CHART -->
    <div class="p-4 bg-white rounded shadow">
      <h2 class="mb-4 text-xl font-bold">
        Grafik Total Mahasiswa per Program Studi
      </h2>

      <apexchart
        width="100%"
        height="460"
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>



<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import VueApexCharts from 'vue3-apexcharts'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  components: {
    Head,
    Link,
    apexchart: VueApexCharts,
  },

  layout: Layout,

  props: {
    report: Array,
    filters: Object,
    periodes: Array,
  },

  data() {
    return {
      form: {
        periode: parseInt(this.filters.periode) || null,
      },
      sort: {
        field: 'total',
        direction: 'desc',
      },
    }
  },

  computed: {
    exportUrl() {
      const params = new URLSearchParams()
      if (this.form.periode) params.set('periode', this.form.periode)
      return this.route('report.prodi.export') + '?' + params.toString()
    },

    prodiLabels() {
      return this.report.map(r => r.prodi)
    },
    sortedReport() {
      return [...this.report].sort((a, b) => {
        const field = this.sort.field
        const dir = this.sort.direction === 'asc' ? 1 : -1

        let x = a[field]
        let y = b[field]

        // angka
        if (['total', 'laki', 'perempuan'].includes(field)) {
          x = Number(x)
          y = Number(y)
        }

        // string
        if (typeof x === 'string') x = x.toLowerCase()
        if (typeof y === 'string') y = y.toLowerCase()

        if (x < y) return -1 * dir
        if (x > y) return 1 * dir
        return 0
      })
    },

    chartSeries() {
      return [
        {
          name: 'Total',
          data: this.report.map(r => r.total),
        },
        {
          name: 'Laki-laki',
          data: this.report.map(r => r.laki),
        },
        {
          name: 'Perempuan',
          data: this.report.map(r => r.perempuan),
        },
      ]
    },

    chartOptions() {
      return {
        chart: { toolbar: { show: false } },
        xaxis: {
          categories: this.prodiLabels,
          labels: { rotate: -45 },
        },
        dataLabels: { enabled: true },
        colors: ['#3b82f6', '#1d4ed8', '#db2777'],
        legend: { position: 'top' },
      }
    },
  },

  watch: {
    form: {
      handler: throttle(function () {
        const query = pickBy(this.form)

        this.$inertia.get(
          this.route('report.prodi'),
          query,
          {
            preserveState: true,
            preserveScroll: true,
          },
        )
      }, 150),
      deep: true,
    },
  },

  methods: {
    toggleSort(field) {
      if (this.sort.field === field) {
        this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.field = field
        this.sort.direction = 'asc'
      }
    },
  },

}
</script>
